import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";
import {
  Plus, Pencil, Trash2, Target, TrendingUp, Loader2, GraduationCap, Layers, CheckCircle2, Activity, Check, ListChecks, Palette,
} from "lucide-react";

const STATUS = {
  active: { label: "Devam Ediyor", cls: "bg-violet-100 text-violet-700" },
  completed: { label: "Tamamlandı", cls: "bg-emerald-100 text-emerald-700" },
  paused: { label: "Duraklatıldı", cls: "bg-amber-100 text-amber-700" },
};
const ALL = "__all__";
const uid = () => Math.random().toString(36).slice(2);
const emptyGoal = {
  area: "", title: "", description: "", start_date: "", target_date: "",
  start_level: "", target_level: "", measurement_method: "", progress: 0,
  subgoals: [], source: "manual", template_id: null,
};

export default function BEP() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [areas, setAreas] = useState([]);
  const [categories, setCategories] = useState([]);
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [areaFilter, setAreaFilter] = useState(ALL);

  // goal dialog
  const [goalOpen, setGoalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [gForm, setGForm] = useState(emptyGoal);
  const [templates, setTemplates] = useState([]);
  const [savingGoal, setSavingGoal] = useState(false);
  const [deleteGoal, setDeleteGoal] = useState(null);

  // evaluation dialog
  const [progOpen, setProgOpen] = useState(false);
  const [progGoal, setProgGoal] = useState(null);
  const [progEntries, setProgEntries] = useState([]);
  const [evalDate, setEvalDate] = useState(new Date().toISOString().slice(0, 10));
  const [evalDone, setEvalDone] = useState([]);
  const [evalNote, setEvalNote] = useState("");
  const [pForm, setPForm] = useState({ date: new Date().toISOString().slice(0, 10), progress: 0, note: "" });
  const [savingProg, setSavingProg] = useState(false);

  const colorOf = (name) => categories.find((c) => c.name === name)?.color || "#7C3AED";

  const loadCategories = async () => {
    try { const { data } = await api.get("/bep/categories"); setCategories(data); setAreas(data.map((c) => c.name)); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    Promise.all([api.get("/students"), api.get("/bep/categories")])
      .then(([s, c]) => {
        setStudents(s.data); setCategories(c.data); setAreas(c.data.map((x) => x.name));
        if (s.data.length) setStudentId(s.data[0].id);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const loadPlan = async (sid) => {
    if (!sid) return;
    setLoading(true);
    try {
      const [g, sum] = await Promise.all([
        api.get(`/bep/goals?student_id=${sid}`),
        api.get(`/bep/students/${sid}/summary`),
      ]);
      setGoals(g.data); setSummary(sum.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPlan(studentId); setAreaFilter(ALL); }, [studentId]);

  const filteredGoals = useMemo(
    () => areaFilter === ALL ? goals : goals.filter((g) => g.area === areaFilter),
    [goals, areaFilter]
  );

  // ---- Goal dialog ----
  const openCreateGoal = () => { setEditingGoal(null); setGForm(emptyGoal); setTemplates([]); setGoalOpen(true); };
  const openEditGoal = (g) => {
    setEditingGoal(g);
    setGForm({ ...emptyGoal, ...g, subgoals: (g.subgoals || []).map((s) => ({ ...s })), source: g.source || "manual" });
    setGoalOpen(true);
  };
  const loadTemplates = async (area) => {
    try { const { data } = await api.get(`/bep/templates?area=${encodeURIComponent(area)}`); setTemplates(data); }
    catch { setTemplates([]); }
  };
  const onAreaChange = (area) => {
    setGForm((f) => ({ ...f, area, template_id: null }));
    if (gForm.source === "template") loadTemplates(area);
  };
  const onSourceChange = (source) => {
    setGForm((f) => ({ ...f, source, template_id: null }));
    if (source === "template" && gForm.area) loadTemplates(gForm.area);
  };
  const onTemplatePick = (tid) => {
    const t = templates.find((x) => x.id === tid);
    const tplSubs = (t?.subgoals || []).filter(Boolean);
    setGForm((f) => ({
      ...f, template_id: tid,
      title: t?.title || f.title,
      description: t?.description || f.description,
      measurement_method: t?.measurement_method || f.measurement_method,
      subgoals: tplSubs.length ? tplSubs.map((s) => ({ title: s, done: false })) : f.subgoals,
    }));
    if (tplSubs.length) toast.success(`${tplSubs.length} alt hedef şablondan aktarıldı`);
  };
  // subgoal editor helpers
  const addSubgoal = () => setGForm((f) => ({ ...f, subgoals: [...(f.subgoals || []), { _k: uid(), title: "", done: false }] }));
  const setSubgoal = (i, v) => setGForm((f) => { const arr = [...(f.subgoals || [])]; arr[i] = { ...arr[i], title: v }; return { ...f, subgoals: arr }; });
  const removeSubgoal = (i) => setGForm((f) => ({ ...f, subgoals: (f.subgoals || []).filter((_, idx) => idx !== i) }));

  const submitGoal = async (e) => {
    e.preventDefault();
    if (!gForm.area || !gForm.title) { toast.error("Alan ve hedef başlığı zorunlu"); return; }
    setSavingGoal(true);
    const cleanSubs = (gForm.subgoals || []).filter((s) => (s.title || "").trim()).map((s) => ({ id: s.id, title: s.title.trim(), done: !!s.done }));
    try {
      if (editingGoal) {
        await api.put(`/bep/goals/${editingGoal.id}`, {
          area: gForm.area, title: gForm.title, description: gForm.description,
          start_date: gForm.start_date, target_date: gForm.target_date,
          start_level: gForm.start_level, target_level: gForm.target_level,
          measurement_method: gForm.measurement_method, subgoals: cleanSubs,
        });
        toast.success("Hedef güncellendi");
      } else {
        await api.post("/bep/goals", { ...gForm, subgoals: cleanSubs, student_id: studentId, progress: Number(gForm.progress) || 0 });
        toast.success("Hedef eklendi");
      }
      setGoalOpen(false); loadPlan(studentId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingGoal(false); }
  };
  const confirmDeleteGoal = async () => {
    try { await api.delete(`/bep/goals/${deleteGoal.id}`); toast.success("Hedef silindi"); setDeleteGoal(null); loadPlan(studentId); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  // ---- Evaluation / Progress dialog ----
  const openProgress = async (g) => {
    setProgGoal(g);
    setEvalDate(new Date().toISOString().slice(0, 10));
    setEvalDone((g.subgoals || []).filter((s) => s.done).map((s) => s.id));
    setEvalNote("");
    setPForm({ date: new Date().toISOString().slice(0, 10), progress: g.progress || 0, note: "" });
    setProgOpen(true);
    await loadProgress(g.id);
  };
  const loadProgress = async (gid) => {
    try { const { data } = await api.get(`/bep/goals/${gid}/progress`); setProgEntries(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  const toggleDone = (id) => setEvalDone((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const submitEvaluate = async () => {
    setSavingProg(true);
    try {
      await api.post(`/bep/goals/${progGoal.id}/evaluate`, { date: evalDate, done_ids: evalDone, note: evalNote });
      toast.success("Değerlendirme kaydedildi ✓");
      setEvalNote("");
      await loadProgress(progGoal.id);
      loadPlan(studentId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingProg(false); }
  };
  const submitManualProgress = async (e) => {
    e.preventDefault();
    setSavingProg(true);
    try {
      await api.post(`/bep/goals/${progGoal.id}/progress`, { date: pForm.date, progress: Number(pForm.progress), note: pForm.note });
      toast.success("İlerleme kaydedildi");
      setPForm((f) => ({ ...f, note: "" }));
      await loadProgress(progGoal.id);
      loadPlan(studentId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSavingProg(false); }
  };

  const chartData = useMemo(
    () => (summary?.areas || []).map((a) => ({ alan: a.area.split(" ")[0], "İlerleme": a.avg_progress, color: a.color || colorOf(a.area) })),
    [summary, categories]  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const lineData = useMemo(
    () => progEntries.map((p) => ({ tarih: p.date?.slice(5), "İlerleme": p.progress })),
    [progEntries]
  );

  const selectedStudent = students.find((s) => s.id === studentId);
  const evalTotal = (progGoal?.subgoals || []).length;
  const evalPct = evalTotal ? Math.round(evalDone.length / evalTotal * 100) : 0;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="bep-page">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Bireyselleştirilmiş Eğitim Planı</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">BEP Modülü</h1>
          <p className="mt-2 text-sm text-slate-600">Hedefleri ve alt hedefleri yönet, tikleyerek değerlendir, kategorileri düzenle.</p>
        </header>

        <Tabs defaultValue="plan" className="w-full">
          <TabsList className="mb-6 rounded-xl bg-slate-100">
            <TabsTrigger value="plan" data-testid="tab-plan" className="rounded-lg data-[state=active]:bg-white">Öğrenci Planı</TabsTrigger>
            <TabsTrigger value="havuz" data-testid="tab-havuz" className="rounded-lg data-[state=active]:bg-white">Şablon Havuzu</TabsTrigger>
            <TabsTrigger value="kategoriler" data-testid="tab-categories" className="rounded-lg data-[state=active]:bg-white">Kategoriler</TabsTrigger>
          </TabsList>

          {/* ============ PLAN TAB ============ */}
          <TabsContent value="plan">
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <div className="min-w-[220px] flex-1">
                <Select value={studentId} onValueChange={setStudentId}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="bep-student-select">
                    <SelectValue placeholder="Öğrenci seç" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={openCreateGoal} disabled={!studentId} data-testid="add-goal-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700 active:scale-[0.98]">
                <Plus className="mr-1.5 h-4 w-4" /> Hedef Ekle
              </Button>
            </div>

            {!studentId ? (
              <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500"><GraduationCap className="mx-auto mb-3 h-10 w-10 text-slate-300" />Önce bir öğrenci ekleyin.</Card>
            ) : loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : (
              <>
                {/* Summary + chart */}
                <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="grid grid-cols-2 gap-4 lg:col-span-1">
                    <StatCard icon={Activity} color="from-violet-500 to-violet-600" label="Genel İlerleme" value={`%${summary?.overall_progress ?? 0}`} />
                    <StatCard icon={Target} color="from-blue-500 to-blue-600" label="Toplam Hedef" value={summary?.total_goals ?? 0} />
                    <StatCard icon={TrendingUp} color="from-fuchsia-500 to-pink-600" label="Aktif" value={summary?.active_goals ?? 0} />
                    <StatCard icon={CheckCircle2} color="from-emerald-500 to-teal-600" label="Tamamlanan" value={summary?.completed_goals ?? 0} />
                  </div>
                  <Card className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] lg:col-span-2" data-testid="bep-area-chart">
                    <h3 className="mb-4 font-heading text-sm font-semibold text-slate-700">Kategori Bazında Ortalama İlerleme</h3>
                    {chartData.length === 0 ? (
                      <p className="py-10 text-center text-sm text-slate-400">Henüz veri yok. Hedef ekleyin.</p>
                    ) : (
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={chartData} margin={{ top: 0, right: 8, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                          <XAxis dataKey="alan" tick={{ fontSize: 11, fill: "#64748b" }} />
                          <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
                          <Tooltip />
                          <Bar dataKey="İlerleme" radius={[6, 6, 0, 0]} maxBarSize={40}>
                            {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </div>

                {/* Area filter */}
                <div className="mb-4 flex flex-wrap gap-2">
                  <FilterChip active={areaFilter === ALL} onClick={() => setAreaFilter(ALL)}>Tüm Alanlar</FilterChip>
                  {areas.map((a) => <FilterChip key={a} active={areaFilter === a} onClick={() => setAreaFilter(a)}>{a}</FilterChip>)}
                </div>

                {/* Goals */}
                {filteredGoals.length === 0 ? (
                  <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="bep-empty"><Target className="mx-auto mb-3 h-10 w-10 text-slate-300" />Bu alanda hedef yok. “Hedef Ekle” ile başla.</Card>
                ) : (
                  <div className="space-y-3">
                    {filteredGoals.map((g) => {
                      const st = STATUS[g.status] || STATUS.active;
                      const subs = g.subgoals || [];
                      const doneCount = subs.filter((s) => s.done).length;
                      return (
                        <Card key={g.id} data-testid={`goal-card-${g.id}`} className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="mb-1 flex flex-wrap items-center gap-2">
                                <Badge className="rounded-full text-white hover:opacity-90" style={{ backgroundColor: colorOf(g.area) }}>{g.area}</Badge>
                                <Badge className={`rounded-full ${st.cls} hover:${st.cls}`}>{st.label}</Badge>
                                {subs.length > 0 && <span className="inline-flex items-center gap-1 text-xs text-slate-400"><ListChecks className="h-3.5 w-3.5" /> {doneCount}/{subs.length} alt hedef</span>}
                              </div>
                              <h3 className="font-heading text-base font-semibold text-slate-900">{g.title}</h3>
                              {g.description && <p className="mt-0.5 text-sm text-slate-500">{g.description}</p>}
                              <div className="mt-3 flex items-center gap-3">
                                <Progress value={g.progress} className="h-2 flex-1" />
                                <span className="w-10 text-right text-sm font-semibold text-slate-700">%{g.progress}</span>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => openProgress(g)} data-testid={`progress-goal-${g.id}`} className="rounded-lg"><ListChecks className="mr-1 h-3.5 w-3.5" /> Değerlendir</Button>
                              <Button variant="ghost" size="icon" onClick={() => openEditGoal(g)} data-testid={`edit-goal-${g.id}`} className="h-9 w-9 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => setDeleteGoal(g)} data-testid={`delete-goal-${g.id}`} className="h-9 w-9 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* ============ HAVUZ TAB ============ */}
          <TabsContent value="havuz">
            <TemplatePool areas={areas} />
          </TabsContent>

          {/* ============ KATEGORILER TAB ============ */}
          <TabsContent value="kategoriler">
            <CategoryManager categories={categories} reload={loadCategories} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Goal dialog */}
      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="goal-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editingGoal ? "Hedef Düzenle" : "Yeni Hedef"}</DialogTitle>
            <DialogDescription>{selectedStudent?.full_name} için bireysel hedef.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitGoal} className="space-y-4 py-2">
            {!editingGoal && (
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-100 p-1" data-testid="goal-source-toggle">
                <button type="button" onClick={() => onSourceChange("manual")} data-testid="source-manual" className={`rounded-lg py-2 text-sm font-medium transition-colors ${gForm.source === "manual" ? "bg-white text-violet-700 shadow-sm" : "text-slate-500"}`}>Manuel / Özel Yaz</button>
                <button type="button" onClick={() => onSourceChange("template")} data-testid="source-template" className={`rounded-lg py-2 text-sm font-medium transition-colors ${gForm.source === "template" ? "bg-white text-violet-700 shadow-sm" : "text-slate-500"}`}>Havuzdan Seç</button>
              </div>
            )}
            <div className="space-y-2">
              <Label>Gelişim Kategorisi *</Label>
              <Select value={gForm.area} onValueChange={onAreaChange}>
                <SelectTrigger data-testid="goal-area-select"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
                <SelectContent>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!editingGoal && gForm.source === "template" && (
              <div className="space-y-2">
                <Label>Havuzdan Hedef Seç</Label>
                <Select value={gForm.template_id || ""} onValueChange={onTemplatePick} disabled={!gForm.area}>
                  <SelectTrigger data-testid="goal-template-select"><SelectValue placeholder={gForm.area ? "Şablon seç" : "Önce kategori seç"} /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}{(t.subgoals || []).length ? ` · ${t.subgoals.length} alt hedef` : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2"><Label>Hedef Başlığı *</Label><Input value={gForm.title} onChange={(e) => setGForm({ ...gForm, title: e.target.value })} data-testid="goal-title-input" /></div>
            <div className="space-y-2"><Label>Açıklama / Ölçüt</Label><Textarea value={gForm.description} onChange={(e) => setGForm({ ...gForm, description: e.target.value })} data-testid="goal-desc-input" /></div>

            {/* Subgoals editor */}
            <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid="subgoals-editor">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-violet-500" /> Alt Hedefler</Label>
                <span className="text-xs text-slate-400">{(gForm.subgoals || []).length} madde</span>
              </div>
              {(gForm.subgoals || []).map((sg, i) => (
                <div key={sg.id || sg._k || i} className="flex items-center gap-2">
                  <Input value={sg.title} onChange={(e) => setSubgoal(i, e.target.value)} placeholder={`Örn: Aynı ${i % 2 ? "resimleri" : "nesneleri"} eşler`} data-testid={`subgoal-input-${i}`} className="h-9" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeSubgoal(i)} data-testid={`subgoal-remove-${i}`} className="h-9 w-9 flex-shrink-0 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addSubgoal} data-testid="subgoal-add" className="h-9 w-full rounded-lg border-dashed"><Plus className="mr-1 h-4 w-4" /> Alt Hedef Ekle</Button>
              <p className="text-xs text-slate-400">Alt hedef eklerseniz yüzde tikli maddelere göre otomatik hesaplanır. Havuzdan hedef seçince alt hedefler otomatik gelir.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Başlangıç Tarihi</Label><Input type="date" value={gForm.start_date || ""} onChange={(e) => setGForm({ ...gForm, start_date: e.target.value })} data-testid="goal-start-input" /></div>
              <div className="space-y-2"><Label>Hedef Tarihi</Label><Input type="date" value={gForm.target_date || ""} onChange={(e) => setGForm({ ...gForm, target_date: e.target.value })} data-testid="goal-target-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Ölçüm Yöntemi</Label><Input value={gForm.measurement_method || ""} onChange={(e) => setGForm({ ...gForm, measurement_method: e.target.value })} placeholder="Örn. Gözlem Formu" data-testid="goal-method-input" /></div>
              {!editingGoal && (gForm.subgoals || []).filter((s) => (s.title || "").trim()).length === 0 &&
                <div className="space-y-2"><Label>Başlangıç İlerlemesi (%)</Label><Input type="number" min="0" max="100" value={gForm.progress} onChange={(e) => setGForm({ ...gForm, progress: e.target.value })} data-testid="goal-progress-input" /></div>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setGoalOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={savingGoal} data-testid="goal-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">{savingGoal ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Evaluation dialog */}
      <Dialog open={progOpen} onOpenChange={setProgOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Değerlendirme</DialogTitle>
            <DialogDescription>{progGoal?.title}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* history chart */}
            <Card className="rounded-xl border-slate-200/60 p-4">
              {lineData.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">Henüz değerlendirme kaydı yok.</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={lineData} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="tarih" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: "#64748b" }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="İlerleme" stroke={colorOf(progGoal?.area)} strokeWidth={2.5} dot={{ r: 3, fill: colorOf(progGoal?.area) }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>

            {evalTotal > 0 ? (
              <div className="rounded-xl bg-slate-50 p-4" data-testid="evaluate-panel">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <Label className="text-xs">Değerlendirme Tarihi</Label>
                    <Input type="date" value={evalDate} onChange={(e) => setEvalDate(e.target.value)} className="h-9 w-40" data-testid="eval-date" />
                  </div>
                  <span className="rounded-full px-3 py-1 text-sm font-bold text-white" style={{ backgroundColor: colorOf(progGoal?.area) }} data-testid="eval-live-pct">{evalDone.length}/{evalTotal} → %{evalPct}</span>
                </div>
                <p className="mb-2 text-xs text-slate-500">Öğrencinin o gün başardığı alt hedefleri tikleyin — yüzde otomatik hesaplanır.</p>
                <div className="space-y-2">
                  {(progGoal?.subgoals || []).map((s) => {
                    const on = evalDone.includes(s.id);
                    return (
                      <button type="button" key={s.id} onClick={() => toggleDone(s.id)} data-testid={`eval-subgoal-${s.id}`}
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                        <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${on ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                        <span className={on ? "font-medium text-slate-800" : "text-slate-600"}>{s.title}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-3 space-y-1"><Label className="text-xs">Not (opsiyonel)</Label><Input value={evalNote} onChange={(e) => setEvalNote(e.target.value)} placeholder="Gözlem notu" className="h-9" data-testid="eval-note" /></div>
                <Button onClick={submitEvaluate} disabled={savingProg} data-testid="eval-save-button" className="mt-3 h-10 w-full rounded-lg bg-violet-600 text-white hover:bg-violet-700">{savingProg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Değerlendirmeyi Kaydet"}</Button>
              </div>
            ) : (
              <form onSubmit={submitManualProgress} className="grid grid-cols-1 gap-3 rounded-xl bg-slate-50 p-4 sm:grid-cols-4" data-testid="add-progress-form">
                <div className="sm:col-span-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">Bu hedefin alt hedefi yok. Tikleyerek otomatik hesaplama için hedefi düzenleyip alt hedef ekleyin ya da manuel yüzde girin.</div>
                <div className="space-y-1"><Label className="text-xs">Tarih</Label><Input type="date" value={pForm.date} onChange={(e) => setPForm({ ...pForm, date: e.target.value })} className="h-9" data-testid="prog-date" /></div>
                <div className="space-y-1"><Label className="text-xs">İlerleme %</Label><Input type="number" min="0" max="100" value={pForm.progress} onChange={(e) => setPForm({ ...pForm, progress: e.target.value })} className="h-9" data-testid="prog-value" /></div>
                <div className="space-y-1 sm:col-span-2"><Label className="text-xs">Not</Label><Input value={pForm.note} onChange={(e) => setPForm({ ...pForm, note: e.target.value })} placeholder="Gözlem notu" className="h-9" data-testid="prog-note" /></div>
                <div className="sm:col-span-4"><Button type="submit" disabled={savingProg} data-testid="prog-save-button" className="h-9 w-full rounded-lg bg-violet-600 text-white hover:bg-violet-700 sm:w-auto">{savingProg ? <Loader2 className="h-4 w-4 animate-spin" /> : "İlerleme Kaydet"}</Button></div>
              </form>
            )}

            {/* entries list */}
            <div className="space-y-2">
              {progEntries.slice().reverse().map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <span className="w-24 text-slate-500">{p.date}</span>
                  <Badge className="rounded-full bg-violet-100 text-violet-700 hover:bg-violet-100">%{p.progress}</Badge>
                  <span className="truncate text-slate-600">{p.note}</span>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteGoal} onOpenChange={(o) => !o && setDeleteGoal(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-goal-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hedefi sil?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteGoal?.title}</strong> ve tüm ilerleme kayıtları silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteGoal} data-testid="confirm-delete-goal" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <Card className="rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className={`mb-2 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${color}`}><Icon className="h-4 w-4 text-white" /></div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </Card>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? "bg-violet-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{children}</button>
  );
}

// ---------------- Category management ----------------
function CategoryManager({ categories, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#7C3AED" });
  const [saving, setSaving] = useState(false);
  const [forceDel, setForceDel] = useState(null);

  const openCreate = () => { setEditing(null); setForm({ name: "", color: "#7C3AED" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, color: c.color || "#7C3AED" }); setOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Kategori adı zorunlu"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/bep/categories/${editing.id}`, form); toast.success("Kategori güncellendi"); }
      else { await api.post("/bep/categories", form); toast.success("Kategori eklendi"); }
      setOpen(false); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const doDelete = async (cat, force) => {
    try {
      await api.delete(`/bep/categories/${cat.id}${force ? "?force=true" : ""}`);
      toast.success("Kategori silindi"); setForceDel(null); reload();
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) { setForceDel({ cat, detail: formatApiErrorDetail(err.response?.data?.detail) }); }
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="category-manager">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-600">BEP gelişim kategorilerini yönetin. Ad değişikliği tüm hedeflere yansır.</p>
        <Button onClick={openCreate} data-testid="add-category-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700"><Plus className="mr-1.5 h-4 w-4" /> Kategori Ekle</Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((c) => (
          <Card key={c.id} data-testid={`category-card-${c.id}`} className="flex items-center gap-3 rounded-2xl border-slate-200/60 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <span className="h-8 w-8 flex-shrink-0 rounded-xl" style={{ backgroundColor: c.color }} />
            <span className="min-w-0 flex-1 truncate font-heading font-semibold text-slate-900">{c.name}</span>
            <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-category-${c.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" onClick={() => doDelete(c, false)} data-testid={`delete-category-${c.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
          </Card>
        ))}
        {categories.length === 0 && <Card className="col-span-full rounded-2xl border-dashed p-14 text-center text-slate-400"><Layers className="mx-auto mb-3 h-9 w-9 text-slate-300" />Henüz kategori yok.</Card>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md" data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Kategori Düzenle" : "Yeni Kategori"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2"><Label>Kategori Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name-input" placeholder="Örn: Bilişsel Beceriler" /></div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Palette className="h-4 w-4 text-violet-500" /> Renk</Label>
              <div className="flex items-center gap-3">
                <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} data-testid="category-color-input" className="h-10 w-16 cursor-pointer rounded-lg border border-slate-200" />
                <span className="text-sm text-slate-500">{form.color}</span>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="category-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!forceDel} onOpenChange={(o) => !o && setForceDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="force-delete-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategoriyi hedefleriyle sil?</AlertDialogTitle>
            <AlertDialogDescription>{forceDel?.detail || "Bu kategoride hedefler var."} Onaylarsanız kategori ve bağlı tüm hedefler + ilerleme kayıtları silinecek.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDelete(forceDel.cat, true)} data-testid="confirm-force-delete-category" className="rounded-xl bg-red-600 hover:bg-red-700">Evet, Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------- Template Pool management ----------------
function TemplatePool({ areas }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ area: "", title: "", description: "", measurement_method: "", subgoals: [] });
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/bep/templates"); setTemplates(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = filter === "__all__" ? templates : templates.filter((t) => t.area === filter);

  const openCreate = () => { setEditing(null); setForm({ area: "", title: "", description: "", measurement_method: "", subgoals: [] }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ area: t.area, title: t.title, description: t.description || "", measurement_method: t.measurement_method || "", subgoals: (t.subgoals || []).map((s) => ({ k: uid(), v: s })) }); setOpen(true); };
  const addTSub = () => setForm((f) => ({ ...f, subgoals: [...(f.subgoals || []), { k: uid(), v: "" }] }));
  const setTSub = (i, v) => setForm((f) => { const a = [...(f.subgoals || [])]; a[i] = { ...a[i], v }; return { ...f, subgoals: a }; });
  const rmTSub = (i) => setForm((f) => ({ ...f, subgoals: (f.subgoals || []).filter((_, x) => x !== i) }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.area || !form.title) { toast.error("Alan ve başlık zorunlu"); return; }
    setSaving(true);
    const payload = { ...form, subgoals: (form.subgoals || []).map((s) => (s.v || "").trim()).filter(Boolean) };
    try {
      if (editing) { await api.put(`/bep/templates/${editing.id}`, payload); toast.success("Şablon güncellendi"); }
      else { await api.post("/bep/templates", payload); toast.success("Şablon eklendi"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  const confirmDel = async () => {
    try { await api.delete(`/bep/templates/${del.id}`); toast.success("Şablon silindi"); setDel(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <div data-testid="template-pool">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-1 flex-wrap gap-2">
          <FilterChip active={filter === "__all__"} onClick={() => setFilter("__all__")}>Tüm Alanlar</FilterChip>
          {areas.map((a) => <FilterChip key={a} active={filter === a} onClick={() => setFilter(a)}>{a}</FilterChip>)}
        </div>
        <Button onClick={openCreate} data-testid="add-template-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700"><Plus className="mr-1.5 h-4 w-4" /> Şablon Ekle</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {shown.map((t) => (
            <Card key={t.id} data-testid={`template-card-${t.id}`} className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <div className="mb-2 flex items-center gap-2">
                <Layers className="h-4 w-4 text-violet-500" />
                <Badge className="rounded-full bg-violet-100 text-violet-700 hover:bg-violet-100">{t.area}</Badge>
                {(t.subgoals || []).length > 0 && <span className="inline-flex items-center gap-1 text-xs text-violet-500"><ListChecks className="h-3.5 w-3.5" /> {t.subgoals.length} alt hedef</span>}
              </div>
              <h3 className="font-heading text-base font-semibold text-slate-900">{t.title}</h3>
              {t.description && <p className="mt-1 text-sm text-slate-500">{t.description}</p>}
              {t.measurement_method && <p className="mt-2 text-xs text-slate-400">Ölçüm: {t.measurement_method}</p>}
              <div className="mt-3 flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button variant="ghost" size="icon" onClick={() => setDel(t)} data-testid={`delete-template-${t.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Şablon Düzenle" : "Yeni Şablon"}</DialogTitle><DialogDescription>Havuza hedef ve hazır alt hedef seti ekle.</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2"><Label>Gelişim Kategorisi *</Label>
              <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                <SelectTrigger data-testid="template-area-select"><SelectValue placeholder="Kategori seç" /></SelectTrigger>
                <SelectContent>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Başlık *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="template-title-input" /></div>
            <div className="space-y-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="template-desc-input" /></div>
            <div className="space-y-2"><Label>Ölçüm Yöntemi</Label><Input value={form.measurement_method} onChange={(e) => setForm({ ...form, measurement_method: e.target.value })} data-testid="template-method-input" /></div>

            {/* Template subgoal set editor */}
            <div className="space-y-2 rounded-xl bg-slate-50 p-3" data-testid="template-subgoals-editor">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5"><ListChecks className="h-4 w-4 text-violet-500" /> Hazır Alt Hedefler</Label>
                <span className="text-xs text-slate-400">{(form.subgoals || []).length} madde</span>
              </div>
              {(form.subgoals || []).map((s, i) => (
                <div key={s.k} className="flex items-center gap-2">
                  <Input value={s.v} onChange={(e) => setTSub(i, e.target.value)} placeholder="Örn: Aynı nesneleri eşler" data-testid={`template-subgoal-input-${i}`} className="h-9" />
                  <Button type="button" variant="ghost" size="icon" onClick={() => rmTSub(i)} data-testid={`template-subgoal-remove-${i}`} className="h-9 w-9 flex-shrink-0 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addTSub} data-testid="template-subgoal-add" className="h-9 w-full rounded-lg border-dashed"><Plus className="mr-1 h-4 w-4" /> Alt Hedef Ekle</Button>
              <p className="text-xs text-slate-400">Bu şablon bir hedefe uygulandığında alt hedefler tek tıkla aktarılır.</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="template-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-template-dialog">
          <AlertDialogHeader><AlertDialogTitle>Şablonu sil?</AlertDialogTitle><AlertDialogDescription><strong>{del?.title}</strong> havuzdan silinecek.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDel} data-testid="confirm-delete-template" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
